/** @jsxImportSource @emotion/react */
import { useMemo, useState } from 'react'
import { Tab, Tabs } from '@mui/material'
import Typography from '@mui/material/Typography'
import { Search } from 'components/Input'
import Select from 'components/Select'
import { PaginationPage, PaginationTake } from 'components/Table'
import { FIRST_PAGE } from 'hooks/useQueryWithParams'
import * as statisticsClasses from '../../styles'
import { ALL_REASON_ID, CancellationUsersTab } from './constants'
import { CancelledUsersTable } from './components/CancelledUsersTable'
import { CancellationReasonsSummary } from './components/CancellationReasonsSummary'
import { CancellationTestimonials } from './components/CancellationTestimonials'
import { useCancellationReasonsStatsQuery } from './hooks/useCancellationReasonsStatsQuery'
import { useCancellationUsersQuery } from './hooks/useCancellationUsersQuery'
import * as classes from './styles'
import { MembershipCancellationsProps, ReasonFilterId } from './types'
import { getReasonFilters } from './utils'

const TABS = [
  { id: CancellationUsersTab.Users, title: 'Cancelled Users' },
  { id: CancellationUsersTab.Testimonials, title: 'Testimonials' },
]

export const MembershipCancellations = ({
  platformId,
  platformSlug,
  startDate,
  endDate,
}: MembershipCancellationsProps) => {
  const [selectedTab, setSelectedTab] = useState(CancellationUsersTab.Users)
  const [selectedReasonId, setSelectedReasonId] = useState<ReasonFilterId>(ALL_REASON_ID)
  const [search, setSearch] = useState('')
  const isTestimonialsTab = selectedTab === CancellationUsersTab.Testimonials

  const reasonFilters = useMemo(() => getReasonFilters(platformSlug), [platformSlug])
  const selectedReason = useMemo(
    () => reasonFilters.find(({ id }) => id === selectedReasonId) ?? reasonFilters[0],
    [reasonFilters, selectedReasonId]
  )

  const cancellationReason = selectedReasonId === ALL_REASON_ID ? undefined : selectedReasonId
  const queryFilters = {
    platformId,
    startDate,
    endDate,
    search,
  }
  const statsQuery = useCancellationReasonsStatsQuery({ platformId, startDate, endDate })
  const usersQuery = useCancellationUsersQuery({ ...queryFilters, cancellationReason }, { skip: isTestimonialsTab })
  const testimonialsQuery = useCancellationUsersQuery(
    { ...queryFilters, hasCancellationComment: true },
    { skip: !isTestimonialsTab }
  )
  const activeQuery = isTestimonialsTab ? testimonialsQuery : usersQuery
  const pageCount = Math.max(FIRST_PAGE, activeQuery.pagination.count)
  const errorMessage = isTestimonialsTab ? 'Failed to load testimonials.' : 'Failed to load cancelled users.'

  return (
    <div css={classes.wrapper}>
      <CancellationReasonsSummary
        reasons={statsQuery.reasons}
        totalCancellations={statsQuery.totalCancellations}
        loading={statsQuery.loading}
        hasError={!!statsQuery.error}
      />

      <div css={classes.tableSection}>
        <div css={classes.tableHeader}>
          <Tabs
            value={selectedTab}
            onChange={(_, tab: CancellationUsersTab) => setSelectedTab(tab)}
            css={[statisticsClasses.statisticsTabs, classes.sectionTabs]}
            aria-label="Cancelled users sections"
          >
            {TABS.map((tab) => (
              <Tab
                key={tab.id}
                value={tab.id}
                label={tab.title}
                css={[statisticsClasses.statisticsTab, selectedTab === tab.id && statisticsClasses.activeStatisticsTab]}
              />
            ))}
          </Tabs>

          <div css={classes.tableFilters}>
            {!isTestimonialsTab && (
              <Select
                value={selectedReason}
                onChange={(value) => setSelectedReasonId(value?.id ?? ALL_REASON_ID)}
                items={reasonFilters}
                placeholder="All reasons"
                displaySelectedItemsFirst
                css={[classes.filterSelect, classes.fullWidthForMobile]}
                disabled={activeQuery.loading}
              />
            )}
            <Search value={search} onChange={setSearch} css={[classes.search, classes.fullWidthForMobile]} />
          </div>
        </div>

        {activeQuery.error && <Typography css={classes.messageText}>{errorMessage}</Typography>}

        {isTestimonialsTab ? (
          <CancellationTestimonials users={activeQuery.items} loading={activeQuery.loading} />
        ) : (
          <CancelledUsersTable platformSlug={platformSlug} users={activeQuery.items} loading={activeQuery.loading} />
        )}

        <div css={classes.tableFooter}>
          <PaginationTake take={activeQuery.pagination.take} onTakeChange={activeQuery.pagination.onTakeChange} />
          <PaginationPage
            totalCount={activeQuery.count}
            count={pageCount}
            page={activeQuery.pagination.page}
            onChange={activeQuery.pagination.onChange}
          />
        </div>
      </div>
    </div>
  )
}
